import { CalendarClockIcon } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';

import { CalendarPanel } from './calendar-panel';
import { PickerFooter } from './picker-footer';
import {
  dateToValues,
  minuteStepFor,
  timeToValues,
  valuesToDate,
  valuesToTime,
  type SegmentValues,
} from './segments';
import {
  clampDate,
  clampTime,
  formatDateTime,
  nowIn,
  parseDateTime,
  sameDate,
  type CalendarDate,
  type WallTime,
} from './temporal';
import { TemporalField, type TemporalInputProps, type TemporalPanelApi } from './temporal-field';
import { TimeColumns } from './time-columns';
import { isTimeOptionDisabled, pickTimeOption, type TimeBounds } from './time-range';

export interface DateTimePickerProps extends TemporalInputProps {
  /** Seconds, as on the native input (default 60). Minutes step by `step / 60`. */
  step?: number;
}

interface DateTimeLimit {
  date: CalendarDate;
  time: WallTime;
}

/**
 * A themed `<input type="datetime-local">`: `value` is `YYYY-MM-DDTHH:mm` (or `''`), `min`/`max`
 * too. Drop it in where `<Input type="datetime-local" {...field} />` was. Wall-clock strings
 * only: zoning stays with the form's mappers (`toZonedInput` / `fromZonedInput`).
 */
export function DateTimePicker({ step, ...props }: DateTimePickerProps) {
  const { t } = useTranslation('common');
  const min = parseDateTime(props.min);
  const max = parseDateTime(props.max);
  return (
    <TemporalField
      {...props}
      kind="datetime"
      minuteStep={minuteStepFor(step)}
      triggerIcon={<CalendarClockIcon aria-hidden />}
      triggerLabel={t('dateTime.chooseDateTime')}
      renderPanel={(api) => <DateTimePanel api={api} min={min} max={max} />}
    />
  );
}

/** The time bounds on `date`: min's time on min's day, max's time on max's day. */
function boundsOn(
  date: CalendarDate | null,
  min: DateTimeLimit | null,
  max: DateTimeLimit | null,
): TimeBounds {
  return {
    min: date && min && sameDate(date, min.date) ? min.time : null,
    max: date && max && sameDate(date, max.date) ? max.time : null,
  };
}

function DateTimePanel({
  api,
  min,
  max,
}: {
  api: TemporalPanelApi;
  min: DateTimeLimit | null;
  max: DateTimeLimit | null;
}) {
  const { t } = useTranslation('common');
  const { segments, options } = api;
  const now = nowIn(api.timeZone);
  const date = valuesToDate(segments);
  const bounds = boundsOn(date, min, max);
  const minDate = min?.date ?? null;
  const maxDate = max?.date ?? null;

  /** Date and time together, the time pulled into that day's bounds. */
  const valuesFor = (day: CalendarDate, time: WallTime): SegmentValues => {
    const dayBounds = boundsOn(day, min, max);
    return {
      ...dateToValues(day),
      ...timeToValues(clampTime(time, dayBounds.min, dayBounds.max), options),
    };
  };

  // A day picked before any time gets midnight; a time picked before any day gets today.
  const currentTime = (): WallTime =>
    valuesToTime(segments, options) ??
    pickTimeOption('minute', segments.minute ?? 0, segments, options, { min: null, max: null });

  const nowTime: WallTime = {
    hour: now.time.hour,
    minute: now.time.minute - (now.time.minute % Math.max(1, options.minuteStep)),
  };
  const nowValue = formatDateTime(now.date, nowTime);
  const nowInRange =
    (!min || nowValue >= formatDateTime(min.date, min.time)) &&
    (!max || nowValue <= formatDateTime(max.date, max.time));

  return (
    <div>
      <div className="flex flex-col sm:flex-row">
        <CalendarPanel
          locale={api.locale}
          selected={date}
          today={now.date}
          min={minDate}
          max={maxDate}
          focusRef={api.initialFocusRef}
          escapeRef={api.escapeRef}
          onSelect={(day, intent) => {
            api.update(valuesFor(day, currentTime()));
            if (intent === 'enter') api.close();
          }}
        />
        <div className="relative h-48 border-t sm:h-auto sm:w-[10.5rem] sm:border-t-0 sm:border-l">
          <TimeColumns
            className="sm:absolute sm:inset-0"
            locale={api.locale}
            options={options}
            values={segments}
            isDisabled={(type, option) =>
              isTimeOptionDisabled(type, option, segments, options, bounds)
            }
            onPick={(type, option) => {
              const day = date ?? clampDate(now.date, minDate, maxDate);
              const time = pickTimeOption(type, option, segments, options, boundsOn(day, min, max));
              api.update(valuesFor(day, time));
            }}
            onEnter={api.close}
          />
        </div>
      </div>
      <PickerFooter
        onClear={() => {
          api.update({});
          api.close();
        }}
        nowLabel={t('dateTime.now')}
        nowDisabled={!nowInRange}
        onNow={() => {
          api.update(valuesFor(now.date, nowTime));
          api.close();
        }}
      />
    </div>
  );
}
